import React, { useEffect, useState } from 'react';
import { useApp } from '../contexts/AppContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Camera, AlertTriangle, CheckCircle, XCircle, Coffee, UtensilsCrossed, Cookie, Truck, FileText, Send, RefreshCw, Plus, Search, Ban, RotateCcw } from 'lucide-react';
import ExportBar from '../components/ExportBar';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';


// ── Status Helpers (replacing nested ternaries) ──
const getStatusBadge = (status) => {
  const map = { distributed: 'badge-success', pending: 'badge-warning', in_transit: 'badge-accent', active: 'badge-success', suspended: 'badge-error' };
  return map[status] || 'badge-neutral';
};
const getComplianceBadge = (pct) => pct >= 90 ? 'badge-success' : pct >= 70 ? 'badge-warning' : 'badge-error';
const getPoStatusColor = (po) => po.send_error ? 'var(--color-error)' : (po.sent_via_email && po.sent_via_whatsapp) ? 'var(--color-accent)' : 'var(--color-warning)';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MEAL_TYPES = [
  { value: 'breakfast', labelBn: 'সকালের নাস্তা', labelEn: 'Breakfast', icon: Coffee, color: '#b45309' },
  { value: 'snacks', labelBn: 'স্ন্যাকস', labelEn: 'Snacks', icon: Cookie, color: '#0D7C6E' },
  { value: 'lunch', labelBn: 'দুপুরের খাবার', labelEn: 'Lunch', icon: UtensilsCrossed, color: '#F42A41' },
];

function MealRecordForm() {
  const { t, lang, currentSchool, currentTeacher } = useApp();
  const [form, setForm] = useState({
    grade: '', section: '', beneficiary_count: '', absent_count: '',
    meal_status: 'served', meal_type: 'lunch', supplier_name: '', quality_issue: '',
  });
  const [photoTaken, setPhotoTaken] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const handleSubmit = async () => {
    if (!currentSchool || !currentTeacher) return;
    if (!photoTaken) {
      toast.error(lang === 'bn' ? 'খাবারের ছবি তুলুন' : 'Please take a meal photo first');
      return;
    }
    setSubmitting(true);
    try {
      const data = {
        school_id: currentSchool.school_id,
        teacher_id: currentTeacher.teacher_id,
        grade: parseInt(form.grade) || 1,
        section: form.section || 'A',
        beneficiary_count: parseInt(form.beneficiary_count) || 0,
        absent_count: parseInt(form.absent_count) || 0,
        meal_status: form.meal_status,
        meal_type: form.meal_type,
        meal_photo_s3_key: 'photos/meal/mock-' + Date.now() + '.jpg',
        photo_gps_lat: currentSchool.gps_lat + (Math.random() - 0.5) * 0.002,
        photo_gps_lng: currentSchool.gps_lng + (Math.random() - 0.5) * 0.002,
        supplier_name: form.supplier_name,
        quality_issue: form.quality_issue || null,
      };
      const res = await axios.post(`${API}/meals`, data);
      setResult(res.data);
      toast.success(lang === 'bn' ? 'খাবারের তথ্য সংরক্ষিত হয়েছে' : 'Meal record saved');
    } catch (e) {
      const msg = e.response?.data?.detail || 'Submission failed';
      toast.error(msg);
    }
    setSubmitting(false);
  };

  const selectedMealType = MEAL_TYPES.find(m => m.value === form.meal_type) || MEAL_TYPES[2];

  return (
    <div className="space-y-4" data-testid="meal-record-form">
      {/* Header */}
      <div>
        <h2 style={{ fontSize: 'var(--text-title3)', fontWeight: 600, color: 'var(--color-label-primary)' }}>
          {lang === 'bn' ? 'খাবার রেকর্ড' : 'Record Meal'}
        </h2>
        <p style={{ fontSize: 'var(--text-subhead)', color: 'var(--color-label-secondary)', marginTop: 'var(--space-1)' }}>
          {new Date().toLocaleDateString(lang === 'bn' ? 'bn-BD' : 'en-US', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
        </p>
      </div>

      {/* Meal Type Selection */}
      <div className="card-surface" data-testid="meal-type-selector">
        <label style={{ fontSize: 'var(--text-footnote)', fontWeight: 500, color: 'var(--color-label-secondary)', display: 'block', marginBottom: 'var(--space-3)' }}>
          {lang === 'bn' ? 'খাবারের ধরন' : 'Meal Type'}
        </label>
        <div className="grid grid-cols-3 gap-3">
          {MEAL_TYPES.map(mt => {
            const Icon = mt.icon;
            const isActive = form.meal_type === mt.value;
            return (
              <button key={mt.value} data-testid={`meal-type-${mt.value}`}
                onClick={() => setForm({ ...form, meal_type: mt.value })}
                className="btn" style={{
                  background: isActive ? mt.color : 'var(--color-surface-2)',
                  color: isActive ? 'white' : 'var(--color-label-secondary)',
                  flexDirection: 'column', padding: 'var(--space-4) var(--space-3)',
                  gap: 'var(--space-2)', fontSize: 'var(--text-footnote)', fontWeight: 500,
                  borderRadius: 'var(--radius-md)', width: '100%'
                }}>
                <Icon size={22} />
                {lang === 'bn' ? mt.labelBn : mt.labelEn}
              </button>
            );
          })}
        </div>
      </div>

      {/* Photo Capture */}
      <div className="card-surface">
        <button data-testid="take-meal-photo" onClick={() => setPhotoTaken(true)}
          style={{ width: '100%', padding: 'var(--space-8) var(--space-4)', border: '2px dashed', borderColor: photoTaken ? 'var(--color-success)' : 'var(--color-separator-opaque)',
            borderRadius: 'var(--radius-md)', background: photoTaken ? 'var(--color-success-bg)' : 'transparent',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'var(--space-2)', cursor: 'pointer' }}>
          {photoTaken ? (
            <>
              <CheckCircle size={32} style={{ color: 'var(--color-success)' }} />
              <span style={{ fontSize: 'var(--text-subhead)', color: 'var(--color-success)', fontWeight: 500 }}>
                {lang === 'bn' ? 'ছবি তোলা হয়েছে' : 'Photo captured'}
              </span>
            </>
          ) : (
            <>
              <Camera size={32} style={{ color: 'var(--color-label-tertiary)' }} />
              <span style={{ fontSize: 'var(--text-subhead)', color: 'var(--color-label-secondary)' }}>
                {lang === 'bn' ? 'খাবারের ছবি তুলুন' : 'Take meal photo'}
              </span>
              <span style={{ fontSize: 'var(--text-caption)', color: 'var(--color-label-tertiary)' }}>
                {lang === 'bn' ? 'ছবি বাধ্যতামূলক' : 'Photo is mandatory'}
              </span>
            </>
          )}
        </button>
      </div>

      {/* Form Fields */}
      <div className="card-surface space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label style={{ fontSize: 'var(--text-footnote)', fontWeight: 500, color: 'var(--color-label-secondary)', display: 'block', marginBottom: 'var(--space-2)' }}>
              {lang === 'bn' ? 'শ্রেণি' : 'Grade'}
            </label>
            <select data-testid="meal-grade" value={form.grade} onChange={e => setForm({...form, grade: e.target.value})}
              className="input-filled" style={{ fontSize: 'var(--text-subhead)' }}>
              <option value="">--</option>
              {Array.from({length: 12}, (_, i) => i + 1).map(g => <option key={g} value={g}>{lang === 'bn' ? `শ্রেণি ${g}` : `Grade ${g}`}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 'var(--text-footnote)', fontWeight: 500, color: 'var(--color-label-secondary)', display: 'block', marginBottom: 'var(--space-2)' }}>
              {lang === 'bn' ? 'শাখা' : 'Section'}
            </label>
            <select data-testid="meal-section" value={form.section} onChange={e => setForm({...form, section: e.target.value})}
              className="input-filled" style={{ fontSize: 'var(--text-subhead)' }}>
              <option value="A">{lang === 'bn' ? 'ক' : 'A'}</option>
              <option value="B">{lang === 'bn' ? 'খ' : 'B'}</option>
              <option value="C">{lang === 'bn' ? 'গ' : 'C'}</option>
            </select>
          </div>
        </div>

        <div>
          <label style={{ fontSize: 'var(--text-footnote)', fontWeight: 500, color: 'var(--color-label-secondary)', display: 'block', marginBottom: 'var(--space-2)' }}>
            {lang === 'bn' ? 'উপকারভোগী সংখ্যা' : 'Beneficiary Count'}
          </label>
          <input data-testid="beneficiary-count" type="number" value={form.beneficiary_count}
            onChange={e => setForm({...form, beneficiary_count: e.target.value})}
            className="input-filled" placeholder="0" />
          {currentSchool && (
            <p style={{ fontSize: 'var(--text-caption)', color: 'var(--color-label-tertiary)', marginTop: 'var(--space-1)' }}>
              {lang === 'bn' ? 'মোট শিক্ষার্থী:' : 'Total students:'} {currentSchool.total_students}
            </p>
          )}
        </div>

        <div>
          <label style={{ fontSize: 'var(--text-footnote)', fontWeight: 500, color: 'var(--color-label-secondary)', display: 'block', marginBottom: 'var(--space-2)' }}>
            {lang === 'bn' ? 'অনুপস্থিত সংখ্যা' : 'Absent Count'}
          </label>
          <input data-testid="absent-count" type="number" value={form.absent_count}
            onChange={e => setForm({...form, absent_count: e.target.value})}
            className="input-filled" placeholder="0" />
        </div>

        <div>
          <label style={{ fontSize: 'var(--text-footnote)', fontWeight: 500, color: 'var(--color-label-secondary)', display: 'block', marginBottom: 'var(--space-2)' }}>
            {lang === 'bn' ? 'পরিবেশনের অবস্থা' : 'Serving Status'}
          </label>
          <div className="flex gap-2">
            {['served', 'not_served', 'partial'].map(s => (
              <button key={s} data-testid={`meal-status-${s}`}
                onClick={() => setForm({...form, meal_status: s})}
                className="btn btn-sm" style={{
                  flex: 1, background: form.meal_status === s ? 'var(--color-accent)' : 'var(--color-surface-2)',
                  color: form.meal_status === s ? 'white' : 'var(--color-label-secondary)',
                  fontSize: 'var(--text-footnote)'
                }}>
                {s === 'served' ? (lang === 'bn' ? 'পরিবেশিত' : 'Served') :
                 s === 'not_served' ? (lang === 'bn' ? 'পরিবেশন হয়নি' : 'Not Served') :
                 (lang === 'bn' ? 'আংশিক' : 'Partial')}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label style={{ fontSize: 'var(--text-footnote)', fontWeight: 500, color: 'var(--color-label-secondary)', display: 'block', marginBottom: 'var(--space-2)' }}>
            {lang === 'bn' ? 'সরবরাহকারী' : 'Supplier'}
          </label>
          <input data-testid="supplier-name" type="text" value={form.supplier_name}
            onChange={e => setForm({...form, supplier_name: e.target.value})}
            className="input-filled" placeholder={lang === 'bn' ? 'সরবরাহকারীর নাম' : 'Supplier name'} />
        </div>
      </div>

      {/* Anomaly Alert */}
      {result?.anomaly_flags?.length > 0 && (
        <div style={{ border: '1px solid var(--color-error)', background: 'var(--color-error-bg)', padding: 'var(--space-4)', borderRadius: 'var(--radius-md)' }} data-testid="anomaly-alert">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle size={16} style={{ color: 'var(--color-error)' }} />
            <span style={{ fontSize: 'var(--text-subhead)', fontWeight: 600, color: 'var(--color-error)' }}>
              {lang === 'bn' ? 'অসঙ্গতি সতর্কতা' : 'Anomaly Alert'}
            </span>
          </div>
          {result.anomaly_flags.map((f, i) => (
            <p key={i} style={{ fontSize: 'var(--text-subhead)', color: 'var(--color-error)' }}>{f.detail_bangla}</p>
          ))}
        </div>
      )}

      {/* Submit */}
      {!result && (
        <button data-testid="submit-meal" onClick={handleSubmit} disabled={submitting}
          className="btn w-full" style={{ background: 'var(--color-accent)', color: 'white', fontSize: 'var(--text-body)', padding: 'var(--space-4)' }}>
          {submitting ? (lang === 'bn' ? 'লোড হচ্ছে...' : 'Submitting...') : (lang === 'bn' ? 'খাবারের তথ্য জমা দিন' : `Submit ${selectedMealType.labelEn} Record`)}
        </button>
      )}
      {result && !result.anomaly_flags?.length && (
        <div style={{ textAlign: 'center', padding: 'var(--space-4)', background: 'var(--color-success-bg)', borderRadius: 'var(--radius-sm)', color: 'var(--color-success)', fontSize: 'var(--text-subhead)' }}>
          {lang === 'bn' ? 'খাবারের তথ্য সংরক্ষিত হয়েছে' : 'Meal record saved successfully'}
        </div>
      )}
    </div>
  );
}

function MealAnalytics() {
  const { t, lang } = useApp();
  const [analytics, setAnalytics] = useState([]);
  const [anomalies, setAnomalies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/meals/analytics`),
      axios.get(`${API}/meals`, { params: { anomaly_only: true } })
    ]).then(([a, m]) => { setAnalytics(a.data); setAnomalies(m.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-center py-12" style={{ color: 'var(--color-label-tertiary)' }}>{t('loading')}</div>;

  const mealTypeData = [
    { name: lang === 'bn' ? 'সকালের নাস্তা' : 'Breakfast', value: 35, color: '#b45309' },
    { name: lang === 'bn' ? 'স্ন্যাকস' : 'Snacks', value: 20, color: '#0D7C6E' },
    { name: lang === 'bn' ? 'দুপুরের খাবার' : 'Lunch', value: 45, color: '#F42A41' },
  ];

  return (
    <div className="space-y-6" data-testid="meal-analytics">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
        <div className="stat-card">
          <p className="stat-value" style={{ color: 'var(--color-accent)' }}>{analytics.reduce((a, s) => a + s.total_beneficiaries, 0).toLocaleString()}</p>
          <div className="bi-label"><span className="stat-label">{lang === "bn" ? "মোট উপকারভোগী" : "Total Beneficiaries"}</span><span style={{fontSize:"11px",opacity:0.4,marginTop:2}}>{lang === "bn" ? "Total Beneficiaries" : "মোট উপকারভোগী"}</span></div>
        </div>
        <div className="stat-card">
          <p className="stat-value">{analytics.reduce((a, s) => a + s.total_meals, 0)}</p>
          <div className="bi-label"><span className="stat-label">{lang === "bn" ? "মোট খাবার রেকর্ড" : "Total Meal Records"}</span><span style={{fontSize:"11px",opacity:0.4,marginTop:2}}>{lang === "bn" ? "Total Meal Records" : "মোট খাবার রেকর্ড"}</span></div>
        </div>
        <div className="stat-card">
          <p className="stat-value" style={{ color: 'var(--color-error)' }}>{anomalies.length}</p>
          <div className="bi-label"><span className="stat-label">{lang === "bn" ? "অসঙ্গতি সতর্কতা" : "Anomaly Flags"}</span><span style={{fontSize:"11px",opacity:0.4,marginTop:2}}>{lang === "bn" ? "Anomaly Flags" : "অসঙ্গতি সতর্কতা"}</span></div>
        </div>
        <div className="stat-card">
          <p className="stat-value" style={{ color: 'var(--color-warning)' }}>3</p>
          <div className="bi-label"><span className="stat-label">{lang === "bn" ? "খাবারের ধরন" : "Meal Types"}</span><span style={{fontSize:"11px",opacity:0.4,marginTop:2}}>{lang === "bn" ? "Meal Types" : "খাবারের ধরন"}</span></div>
        </div>
      </div>

      {/* Meal Type Distribution + Per School chart side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="card-surface">
          <h3 style={{ fontSize: 'var(--text-subhead)', fontWeight: 600, color: 'var(--color-label-secondary)', marginBottom: 'var(--space-4)' }}>
            {lang === 'bn' ? 'খাবারের ধরন' : lang === 'bn' ? 'খাবারের ধরন বণ্টন' : 'Meal Type Distribution'}
          </h3>
          <ResponsiveContainer minWidth={1} minHeight={1} width="100%" height={180}>
            <PieChart>
              <Pie data={mealTypeData} cx="50%" cy="50%" innerRadius={40} outerRadius={70} dataKey="value" paddingAngle={4}>
                {mealTypeData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex justify-center gap-4 mt-2">
            {mealTypeData.map(mt => (
              <div key={mt.name} className="flex items-center gap-1">
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: mt.color, display: 'inline-block' }} />
                <span style={{ fontSize: 'var(--text-caption)', color: 'var(--color-label-secondary)' }}>{mt.name}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="card-surface lg:col-span-2">
          <h3 style={{ fontSize: 'var(--text-subhead)', fontWeight: 600, color: 'var(--color-label-secondary)', marginBottom: 'var(--space-4)' }}>
            {lang === 'bn' ? 'বিদ্যালয় ভিত্তিক' : lang === 'bn' ? 'বিদ্যালয় ভিত্তিক' : 'Per School'}
          </h3>
          <ResponsiveContainer minWidth={1} minHeight={1} width="100%" height={200}>
            <BarChart data={analytics}>
              <XAxis dataKey="school_name" tick={{ fontSize: 9, fill: 'var(--color-label-tertiary)' }} angle={-10} />
              <YAxis tick={{ fontSize: 11, fill: 'var(--color-label-tertiary)' }} />
              <Tooltip contentStyle={{ background: 'var(--color-surface-1)', boxShadow: 'var(--shadow-lg)', borderRadius: 'var(--radius-md)', border: 'none' }} />
              <Bar dataKey="total_beneficiaries" fill="var(--color-accent)" name={lang === 'bn' ? 'উপকারভোগী' : 'Beneficiaries'} radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Anomaly Flags */}
      {anomalies.length > 0 && (
        <div className="card-surface">
          <h3 style={{ fontSize: 'var(--text-subhead)', fontWeight: 600, color: 'var(--color-label-secondary)', marginBottom: 'var(--space-4)', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <AlertTriangle size={16} style={{ color: 'var(--color-error)' }} />
            {lang === 'bn' ? 'অসঙ্গতি সতর্কতা' : 'Anomaly Flags'} ({anomalies.length})
          </h3>
          <div className="space-y-3">
            {anomalies.map(m => (
              <div key={m.meal_id} style={{ border: '1px solid var(--color-error-bg)', padding: 'var(--space-3)', borderRadius: 'var(--radius-sm)', borderLeft: '3px solid var(--color-error)' }}>
                <div className="flex items-start justify-between">
                  <div>
                    <p style={{ fontSize: 'var(--text-subhead)', fontWeight: 500, color: 'var(--color-error)' }}>{m.meal_date}</p>
                    {m.anomaly_flags?.map((f, i) => (
                      <p key={i} style={{ fontSize: 'var(--text-subhead)', color: 'var(--color-label-secondary)', marginTop: 2 }}>{f.detail_bangla}</p>
                    ))}
                  </div>
                  <span className="badge badge-error">{lang === 'bn' ? 'মাত্রা' : 'Severity'}: {m.anomaly_flags?.[0]?.severity || 'N/A'}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Menu Planning */}
      <div className="card-surface">
        <h3 style={{fontSize:'var(--text-subhead)',fontWeight:600,color:'var(--color-label-secondary)',marginBottom:'var(--space-4)'}}>
          {lang === 'bn' ? 'সাপ্তাহিক মেনু' : 'Weekly Menu'}
          <span style={{fontSize:'10px',color:'var(--color-label-tertiary)',display:'block',marginTop:2}}>{lang === 'bn' ? 'Weekly Menu' : 'সাপ্তাহিক মেনু'}</span>
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full data-table">
            <thead><tr><th>{lang === 'bn' ? 'দিন' : 'Day'}</th><th>{lang === 'bn' ? 'সকালের নাস্তা' : 'Breakfast'}</th><th>{lang === 'bn' ? 'দুপুরের খাবার' : 'Lunch'}</th><th>{lang === 'bn' ? 'স্ন্যাকস' : 'Snacks'}</th><th>{lang === 'bn' ? 'ক্যালোরি' : 'Calories'}</th></tr></thead>
            <tbody>
              {[{day:'Sunday',dayBn:'রবিবার',b:'Bread, Egg, Milk',l:'Rice, Fish Curry, Dal, Vegetables',s:'Banana',cal:850},{day:'Monday',dayBn:'সোমবার',b:'Paratha, Vegetables',l:'Rice, Chicken, Lentil Soup',s:'Seasonal Fruit',cal:920},{day:'Tuesday',dayBn:'মঙ্গলবার',b:'Rice Flakes, Jaggery',l:'Rice, Egg Curry, Mixed Veg',s:'Biscuits, Milk',cal:780},{day:'Wednesday',dayBn:'বুধবার',b:'Bread, Dal',l:'Khichuri, Egg, Salad',s:'Puffed Rice Mix',cal:810},{day:'Thursday',dayBn:'বৃহস্পতিবার',b:'Semolina Pudding',l:'Rice, Fish, Spinach',s:'Banana',cal:870}].map(m => (
                <tr key={m.day}><td>{lang === 'bn' ? m.dayBn : m.day}</td><td style={{fontSize:'var(--text-footnote)'}}>{m.b}</td><td style={{fontSize:'var(--text-footnote)'}}>{m.l}</td><td style={{fontSize:'var(--text-footnote)'}}>{m.s}</td><td><span className="badge badge-accent">{m.cal}</span></td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Nutritional Analysis */}
      <div className="card-surface">
        <h3 style={{fontSize:'var(--text-subhead)',fontWeight:600,color:'var(--color-label-secondary)',marginBottom:'var(--space-4)'}}>
          {lang === 'bn' ? 'পুষ্টি বিশ্লেষণ' : 'Nutritional Analysis'}
          <span style={{fontSize:'10px',color:'var(--color-label-tertiary)',display:'block',marginTop:2}}>{lang === 'bn' ? 'Nutritional Analysis' : 'পুষ্টি বিশ্লেষণ'}</span>
        </h3>
        <ResponsiveContainer minWidth={1} minHeight={1} width="100%" height={200}>
          <PieChart><Pie data={[{name:lang==='bn'?'কার্বোহাইড্রেট':'Carbs',value:45,color:'#006A4E'},{name:lang==='bn'?'প্রোটিন':'Protein',value:25,color:'#F42A41'},{name:lang==='bn'?'চর্বি':'Fat',value:15,color:'#b45309'},{name:lang==='bn'?'ফাইবার':'Fiber',value:10,color:'#0D7C6E'},{name:lang==='bn'?'ভিটামিন':'Vitamins',value:5,color:'#6B21A8'}]} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" paddingAngle={3}>
            {[{color:'#006A4E'},{color:'#F42A41'},{color:'#b45309'},{color:'#0D7C6E'},{color:'#6B21A8'}].map((e,i) => <Cell key={i} fill={e.color} />)}
          </Pie><Tooltip /></PieChart>
        </ResponsiveContainer>
        <div style={{display:'flex',justifyContent:'center',gap:'var(--space-4)',flexWrap:'wrap',marginTop:'var(--space-3)'}}>
          {[{n:'Carbs',c:'#006A4E',v:'45%'},{n:'Protein',c:'#F42A41',v:'25%'},{n:'Fat',c:'#b45309',v:'15%'},{n:'Fiber',c:'#0D7C6E',v:'10%'},{n:'Vitamins',c:'#6B21A8',v:'5%'}].map(n => (
            <div key={n.n} style={{display:'flex',alignItems:'center',gap:4}}><span style={{width:8,height:8,borderRadius:'50%',background:n.c,display:'inline-block'}} /><span style={{fontSize:'var(--text-caption)',color:'var(--color-label-secondary)'}}>{n.n} {n.v}</span></div>
          ))}
        </div>
      </div>

      {/* Export Controls */}
      <div style={{display:'flex',justifyContent:'flex-end',marginBottom:'var(--space-3)'}}>
        <ExportBar title="Meal Analytics Report" data={analytics} columns={[
          {label:'School',accessor:'school_name'},{label:'Total Meals',accessor:'total_meals'},
          {label:'Beneficiaries',accessor:'total_beneficiaries'},{label:'Anomalies',accessor:'anomaly_count'}
        ]} />
      </div>

      {/* School Summary Table */}
      <div className="card-surface">
        <h3 style={{ fontSize: 'var(--text-subhead)', fontWeight: 600, color: 'var(--color-label-secondary)', marginBottom: 'var(--space-4)' }}>
          {lang === 'bn' ? 'বিদ্যালয় সারসংক্ষেপ' : lang === 'bn' ? 'বিদ্যালয় খাবার সারসংক্ষেপ' : 'School Meal Summary'}
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full data-table" data-testid="meal-summary-table">
            <thead>
              <tr>
                <th>{lang === 'bn' ? 'বিদ্যালয়' : 'School'}</th>
                <th>{lang === 'bn' ? 'মোট খাবার' : 'Total Meals'}</th>
                <th>{lang === 'bn' ? 'উপকারভোগী' : 'Beneficiaries'}</th>
                <th>{lang === 'bn' ? 'অসঙ্গতি' : 'Anomalies'}</th>
                <th>{lang === 'bn' ? 'সম্মতি' : 'Compliance'}</th>
              </tr>
            </thead>
            <tbody>
              {analytics.map(s => {
                const compliance = s.total_meals > 0 ? Math.round((s.total_meals - s.anomaly_count) / s.total_meals * 100) : 100;
                return (
                  <tr key={s.school_id}>
                    <td>{s.school_name}</td>
                    <td>{s.total_meals}</td>
                    <td>{s.total_beneficiaries.toLocaleString()}</td>
                    <td>{s.anomaly_count > 0 ? <span style={{ color: 'var(--color-error)', fontWeight: 600 }}>{s.anomaly_count}</span> : <CheckCircle size={16} style={{ color: 'var(--color-success)' }} />}</td>
                    <td><span className={`badge ${compliance >= 90 ? 'badge-success' : compliance >= 70 ? 'badge-warning' : 'badge-error'}`}>{compliance}%</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ═══ SUPPLIER MANAGEMENT (District Admin) ═══
function SupplierManagement() {
  const { lang } = useApp();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newSup, setNewSup] = useState({ name: '', whatsapp_number: '', email: '', contract_reference: '', district_id: '' });

  useEffect(() => { axios.get(`${API}/suppliers`).then(r => setSuppliers(r.data)).catch(() => {}).finally(() => setLoading(false)); }, []);

  const handleSuspend = async (id, name) => {
    const reason = prompt(lang === 'bn' ? 'সাসপেন্ডের কারণ লিখুন:' : `Reason for suspending ${name}:`);
    if (!reason) return;
    await axios.post(`${API}/suppliers/${id}/suspend`, { reason });
    toast.success(`${name} suspended`);
    const r = await axios.get(`${API}/suppliers`); setSuppliers(r.data);
  };
  const handleReinstate = async (id, name) => {
    await axios.post(`${API}/suppliers/${id}/reinstate`);
    toast.success(`${name} reinstated`);
    const r = await axios.get(`${API}/suppliers`); setSuppliers(r.data);
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 'var(--space-8)', color: 'var(--color-label-tertiary)' }}>Loading...</div>;

  return (
    <div className="space-y-4">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h3 style={{ fontSize: 'var(--text-body)', fontWeight: 600 }}>{lang === 'bn' ? 'সরবরাহকারী ব্যবস্থাপনা' : 'Supplier Management'}</h3>
          <div style={{ fontSize: 11, color: 'var(--color-label-tertiary)', marginTop: 2 }}>{lang === 'bn' ? 'Supplier Management' : 'সরবরাহকারী ব্যবস্থাপনা'}</div>
        </div>
        <button onClick={() => setShowAdd(!showAdd)} className="btn btn-sm" style={{ background: 'var(--color-accent)', color: 'white' }}>
          <Plus size={14} /> {lang === 'bn' ? 'নতুন সরবরাহকারী' : 'Add Supplier'}
        </button>
      </div>
      {showAdd && (
        <div className="card-surface space-y-3" style={{ borderTop: '3px solid var(--color-accent)' }}>
          <input className="input-filled" placeholder={lang === 'bn' ? 'সরবরাহকারীর নাম' : 'Supplier Name'} value={newSup.name} onChange={e => setNewSup({ ...newSup, name: e.target.value })} />
          <div className="grid grid-cols-2 gap-3">
            <input className="input-filled" placeholder="WhatsApp (+880...)" value={newSup.whatsapp_number} onChange={e => setNewSup({ ...newSup, whatsapp_number: e.target.value })} />
            <input className="input-filled" placeholder="Email" value={newSup.email} onChange={e => setNewSup({ ...newSup, email: e.target.value })} />
          </div>
          <input className="input-filled" placeholder={lang === 'bn' ? 'চুক্তি রেফারেন্স' : 'Contract Reference (e.g., GOB-DHA-2026-001)'} value={newSup.contract_reference} onChange={e => setNewSup({ ...newSup, contract_reference: e.target.value })} />
          <button className="btn w-full" style={{ background: 'var(--color-accent)', color: 'white' }}
            onClick={async () => { await axios.post(`${API}/suppliers`, newSup); toast.success('Supplier added'); setShowAdd(false); const r = await axios.get(`${API}/suppliers`); setSuppliers(r.data); }}>
            {lang === 'bn' ? 'সরবরাহকারী যোগ করুন' : 'Add Supplier'}
          </button>
        </div>
      )}
      {suppliers.map(s => (
        <div key={s.supplier_id} className="card-surface" style={{ padding: 'var(--space-4) var(--space-5)', borderLeft: `3px solid ${s.status === 'active' ? 'var(--color-accent)' : 'var(--color-error)'}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
                <Truck size={16} style={{ color: s.status === 'active' ? 'var(--color-accent)' : 'var(--color-error)' }} />
                <span style={{ fontSize: 'var(--text-body)', fontWeight: 600 }}>{s.name}</span>
                <span className={"badge " + (s.status === 'active' ? 'badge-success' : 'badge-error')}>{s.status.toUpperCase()}</span>
              </div>
              <div style={{ fontSize: 'var(--text-footnote)', color: 'var(--color-label-secondary)' }}>
                {lang === 'bn' ? 'ফোন' : 'WhatsApp'}: {s.whatsapp_number} | Email: {s.email}
              </div>
              <div style={{ fontSize: 'var(--text-footnote)', color: 'var(--color-label-tertiary)', marginTop: 2 }}>
                {lang === 'bn' ? 'চুক্তি' : 'Contract'}: {s.contract_reference || 'N/A'} | {lang === 'bn' ? 'বিদ্যালয়' : 'Schools'}: {(s.assigned_school_ids || []).length}
              </div>
              {s.suspension_reason && <div style={{ fontSize: 'var(--text-footnote)', color: 'var(--color-error)', marginTop: 4 }}>{lang === 'bn' ? 'কারণ' : 'Reason'}: {s.suspension_reason}</div>}
            </div>
            <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
              {s.status === 'active' ? (
                <button onClick={() => handleSuspend(s.supplier_id, s.name)} className="btn btn-sm" style={{ background: 'var(--color-error-bg)', color: 'var(--color-error)', padding: '4px 10px', fontSize: 11 }}><Ban size={12} /> {lang === 'bn' ? 'সাসপেন্ড' : 'Suspend'}</button>
              ) : (
                <button onClick={() => handleReinstate(s.supplier_id, s.name)} className="btn btn-sm" style={{ background: 'var(--color-accent)', color: 'white', padding: '4px 10px', fontSize: 11 }}><RotateCcw size={12} /> {lang === 'bn' ? 'পুনর্বহাল' : 'Reinstate'}</button>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ═══ PO STATUS DASHBOARD (District Admin) ═══
function POStatusDashboard() {
  const { lang } = useApp();
  const [pos, setPOs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { axios.get(`${API}/purchase-orders`).then(r => setPOs(r.data)).catch(() => {}).finally(() => setLoading(false)); }, []);

  const handleResend = async (poId, ref) => {
    await axios.post(`${API}/purchase-orders/${poId}/resend`);
    toast.success(`PO ${ref} resent`);
    const r = await axios.get(`${API}/purchase-orders`); setPOs(r.data);
  };

  const sent = pos.filter(p => p.sent_via_email && p.sent_via_whatsapp).length;
  const partial = pos.filter(p => p.sent_via_email !== p.sent_via_whatsapp).length;
  const failed = pos.filter(p => p.send_error).length;

  return (
    <div className="space-y-4">
      <div>
        <h3 style={{ fontSize: 'var(--text-body)', fontWeight: 600 }}>{lang === 'bn' ? 'ক্রয় আদেশ স্ট্যাটাস' : 'Purchase Order Status'}</h3>
        <div style={{ fontSize: 11, color: 'var(--color-label-tertiary)', marginTop: 2 }}>{lang === 'bn' ? 'PO Status Dashboard' : 'ক্রয় আদেশ স্ট্যাটাস'}</div>
      </div>
      {/* Summary banner */}
      <div className="grid grid-cols-3 gap-4">
        <div className="stat-card" style={{ textAlign: 'center' }}><p className="stat-value" style={{ color: 'var(--color-accent)' }}>{sent}</p><p className="stat-label">{lang === 'bn' ? 'সফলভাবে পাঠানো' : 'Fully Sent'}</p></div>
        <div className="stat-card" style={{ textAlign: 'center' }}><p className="stat-value" style={{ color: 'var(--color-warning)' }}>{partial}</p><p className="stat-label">{lang === 'bn' ? 'আংশিক' : 'Partial'}</p></div>
        <div className="stat-card" style={{ textAlign: 'center' }}><p className="stat-value" style={{ color: 'var(--color-error)' }}>{failed}</p><p className="stat-label">{lang === 'bn' ? 'ব্যর্থ' : 'Failed'}</p></div>
      </div>
      <ExportBar title="Purchase Orders" data={pos} columns={[
        {label:'PO Reference',accessor:'po_reference'},{label:'School',accessor:'school_name'},{label:'Supplier',accessor:'supplier_name'},
        {label:'Meals',accessor:'meal_count'},{label:'Email',accessor:r=>r.sent_via_email?'✓':'✗'},{label:'WhatsApp',accessor:r=>r.sent_via_whatsapp?'✓':'✗'},
        {label:'Date',accessor:'delivery_date'}
      ]} />
      {/* PO List */}
      {pos.map(po => (
        <div key={po.po_id} className="card-surface" style={{ padding: 'var(--space-4) var(--space-5)', borderLeft: `3px solid ${po.send_error ? 'var(--color-error)' : po.sent_via_email && po.sent_via_whatsapp ? 'var(--color-accent)' : 'var(--color-warning)'}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: 'var(--text-body)', fontWeight: 700, color: 'var(--color-accent)' }}>{po.po_reference}</div>
              <div style={{ fontSize: 'var(--text-subhead)', marginTop: 2 }}>{po.school_name || po.school_id}</div>
              <div style={{ fontSize: 'var(--text-footnote)', color: 'var(--color-label-secondary)', marginTop: 4 }}>
                {lang === 'bn' ? 'সরবরাহকারী' : 'Supplier'}: {po.supplier_name || po.supplier_id} | {lang === 'bn' ? 'খাবার' : 'Meals'}: {po.meal_count} | {po.delivery_date}
              </div>
              <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 6 }}>
                <span className={"badge " + (po.sent_via_whatsapp ? 'badge-success' : 'badge-error')}>WA: {po.sent_via_whatsapp ? '✓' : '✗'}</span>
                <span className={"badge " + (po.sent_via_email ? 'badge-success' : 'badge-error')}>Email: {po.sent_via_email ? '✓' : '✗'}</span>
                {po.resend_count > 0 && <span className="badge badge-warning">Resent ×{po.resend_count}</span>}
              </div>
              {po.send_error && <div style={{ fontSize: 'var(--text-footnote)', color: 'var(--color-error)', marginTop: 4 }}>{po.send_error}</div>}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', alignItems: 'flex-end' }}>
              <a href={`${API}/purchase-orders/${po.po_id}/pdf`} target="_blank" rel="noreferrer" className="btn btn-sm" style={{ background: 'var(--color-accent)', color: 'white', padding: '4px 10px', fontSize: 11, textDecoration: 'none' }}><FileText size={12} /> {lang === 'bn' ? 'PO দেখুন' : 'View PO'}</a>
              {(po.send_error || !po.sent_via_whatsapp) && (
                <button onClick={() => handleResend(po.po_id, po.po_reference)} className="btn btn-sm" style={{ background: 'var(--color-warning-bg)', color: 'var(--color-warning)', padding: '4px 10px', fontSize: 11 }}><RefreshCw size={12} /> {lang === 'bn' ? 'পুনরায় পাঠান' : 'Resend'}</button>
              )}
            </div>
          </div>
        </div>
      ))}
      {pos.length === 0 && <div className="card-surface" style={{ textAlign: 'center', padding: 'var(--space-8)', color: 'var(--color-label-tertiary)' }}>{lang === 'bn' ? 'আজকের কোনো PO নেই' : 'No purchase orders for today'}</div>}
    </div>
  );
}

// ═══ PRINCIPAL: SUBMIT TO DISTRICT (NOT Generate PO) ═══
function PrincipalSubmitPanel() {
  const { lang, currentSchool } = useApp();
  const [supplier, setSupplier] = useState(null);
  const [status, setStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentSchool) return;
    Promise.all([
      axios.get(`${API}/schools/${currentSchool.school_id}/supplier`).catch(() => ({ data: { supplier: null } })),
      axios.get(`${API}/schools/${currentSchool.school_id}/attendance-total/status`).catch(() => ({ data: { submitted: false } }))
    ]).then(([s, st]) => {
      setSupplier(s.data.supplier);
      setStatus(st.data);
      setLoading(false);
    });
  }, [currentSchool]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async () => {
    if (!currentSchool) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/schools/${currentSchool.school_id}/attendance-total/submit`, {
        submission_date: new Date().toISOString().slice(0, 10)
      });
      toast.success(res.data.message || (lang === 'bn' ? 'হাজিরা জমা দেওয়া হয়েছে' : 'Attendance submitted to district'));
      setStatus({ submitted: true, submitted_at: res.data.submitted_at, total_present: res.data.total_present, po_generated: false });
    } catch (e) {
      const msg = e.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : msg?.message || 'Submission failed');
    }
    setSubmitting(false);
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 'var(--space-8)', color: 'var(--color-label-tertiary)' }}>Loading...</div>;

  return (
    <div className="space-y-4">
      <div>
        <h3 style={{ fontSize: 'var(--text-body)', fontWeight: 600 }}>{lang === 'bn' ? 'জেলায় হাজিরা জমা দিন' : 'Submit Attendance to District'}</h3>
        <div style={{ fontSize: 11, color: 'var(--color-label-tertiary)', marginTop: 2 }}>{lang === 'bn' ? 'Submit to District' : 'জেলায় হাজিরা জমা'}</div>
      </div>

      {/* Supplier Info (read-only) */}
      <div className="card-surface" style={{ borderTop: '3px solid var(--color-accent)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
          <Truck size={20} style={{ color: 'var(--color-accent)' }} />
          <div>
            <div style={{ fontSize: 'var(--text-subhead)', fontWeight: 600 }}>{supplier ? supplier.name : (lang === 'bn' ? 'কোনো সরবরাহকারী নেই' : 'No Supplier Assigned')}</div>
            {supplier && <div style={{ fontSize: 'var(--text-footnote)', color: 'var(--color-label-secondary)' }}>
              WhatsApp: {supplier.whatsapp_number} | Email: {supplier.email}
            </div>}
            <div style={{ fontSize: 'var(--text-caption)', color: 'var(--color-label-tertiary)', marginTop: 4 }}>{lang === 'bn' ? 'সরবরাহকারী তথ্য শুধুমাত্র পড়ার জন্য' : 'Supplier info is read-only'}</div>
          </div>
        </div>
      </div>

      {/* State: Not Submitted */}
      {!status?.submitted && (
        <div className="card-surface" style={{ padding: 'var(--space-6)', textAlign: 'center' }}>
          <div style={{ fontSize: 'var(--text-title3)', fontWeight: 700, color: 'var(--color-label-primary)', marginBottom: 'var(--space-3)' }}>
            {lang === 'bn' ? 'আজকের হাজিরা জমা দিন' : "Submit Today's Attendance Total"}
          </div>
          <p style={{ fontSize: 'var(--text-subhead)', color: 'var(--color-label-secondary)', marginBottom: 'var(--space-4)', lineHeight: 1.6 }}>
            {lang === 'bn' ? 'বিদ্যালয়ের সমস্ত শ্রেণির উপস্থিতি সংকলন জেলা প্রশাসকের কাছে পাঠানো হবে। জেলা প্রশাসক PO তৈরি করবেন।' : 'School-wide attendance totals will be sent to the District Admin, who will generate the Purchase Order.'}
          </p>
          <button onClick={handleSubmit} disabled={submitting}
            className="btn" style={{ background: 'var(--color-accent)', color: 'white', fontSize: 'var(--text-body)', padding: 'var(--space-4) var(--space-8)' }}>
            <Send size={18} /> {submitting ? '...' : (lang === 'bn' ? 'জেলায় জমা দিন' : 'Submit to District')}
          </button>
        </div>
      )}

      {/* State: Submitted, PO Pending */}
      {status?.submitted && !status?.po_generated && (
        <div className="card-surface" style={{ padding: 'var(--space-6)', textAlign: 'center', background: 'var(--color-warning-bg)', borderLeft: '4px solid var(--color-warning)' }}>
          <Clock size={32} style={{ color: 'var(--color-warning)', margin: '0 auto 12px' }} />
          <div style={{ fontSize: 'var(--text-body)', fontWeight: 600, color: 'var(--color-warning)' }}>
            {lang === 'bn' ? `হাজিরা জমা দেওয়া হয়েছে ${status.submitted_at?.slice(11, 16) || ''}-এ` : `Submitted at ${status.submitted_at?.slice(11, 16) || ''} — awaiting PO from District Admin`}
          </div>
          <p style={{ fontSize: 'var(--text-subhead)', color: 'var(--color-label-secondary)', marginTop: 'var(--space-2)' }}>
            {lang === 'bn' ? `মোট উপস্থিত: ${status.total_present} জন` : `Total present: ${status.total_present} students`}
          </p>
          <p style={{ fontSize: 'var(--text-footnote)', color: 'var(--color-label-tertiary)', marginTop: 'var(--space-2)' }}>
            {lang === 'bn' ? 'পুনরায় জমা দেওয়া সম্ভব নয়। জেলা প্রশাসক PO তৈরি করলে আপনাকে জানানো হবে।' : 'Re-submission is not possible. You will be notified when the District Admin generates the PO.'}
          </p>
        </div>
      )}

      {/* State: PO Generated */}
      {status?.submitted && status?.po_generated && (
        <div className="card-surface" style={{ padding: 'var(--space-6)', textAlign: 'center', background: 'var(--color-success-bg)', borderLeft: '4px solid var(--color-success)' }}>
          <CheckCircle size={32} style={{ color: 'var(--color-success)', margin: '0 auto 12px' }} />
          <div style={{ fontSize: 'var(--text-body)', fontWeight: 600, color: 'var(--color-success)' }}>
            {lang === 'bn' ? 'PO তৈরি হয়েছে' : 'Purchase Order Generated'}
          </div>
          {status.po_reference && <div style={{ fontSize: 'var(--text-title3)', fontWeight: 700, color: 'var(--color-accent)', marginTop: 'var(--space-2)' }}>{status.po_reference}</div>}
          {status.pdf_url && <a href={`${API}${status.pdf_url}`} target="_blank" rel="noreferrer" className="btn btn-sm" style={{ background: 'var(--color-accent)', color: 'white', marginTop: 'var(--space-3)', textDecoration: 'none' }}><FileText size={14} /> {lang === 'bn' ? 'PO দেখুন' : 'View PDF'}</a>}
          <p style={{ fontSize: 'var(--text-footnote)', color: 'var(--color-label-tertiary)', marginTop: 'var(--space-3)' }}>
            {lang === 'bn' ? `মোট উপস্থিত: ${status.total_present} জন` : `Total present: ${status.total_present} students`}
          </p>
        </div>
      )}
    </div>
  );
}

// ═══ DISTRICT ADMIN: PO GENERATION (Single + Bulk) ═══
function DistrictPOGeneration() {
  const { lang } = useApp();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(null);
  const [bulkRunning, setBulkRunning] = useState(false);

  const loadData = async () => {
    try { const r = await axios.get(`${API}/daily-submissions`); setSubmissions(r.data); } catch(e) {}
    setLoading(false);
  };
  useEffect(() => { loadData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const generateSingle = async (schoolId, schoolName) => {
    setGenerating(schoolId);
    try {
      const res = await axios.post(`${API}/purchase-orders/generate`, {
        school_id: schoolId, delivery_date: new Date().toISOString().slice(0, 10)
      });
      toast.success(`PO ${res.data.po_reference} generated for ${schoolName}`);
      await loadData();
    } catch (e) {
      const msg = e.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : msg?.message || 'Generation failed');
    }
    setGenerating(null);
  };

  const generateBulk = async () => {
    setBulkRunning(true);
    try {
      const res = await axios.post(`${API}/purchase-orders/generate-bulk`, {
        delivery_date: new Date().toISOString().slice(0, 10)
      });
      toast.success(`Bulk: ${res.data.processed} POs generated, ${res.data.failed} failed, ${res.data.skipped_no_submission} schools no submission`);
      await loadData();
    } catch (e) { toast.error('Bulk generation failed'); }
    setBulkRunning(false);
  };

  const pendingCount = submissions.filter(s => !s.po_generated).length;
  const doneCount = submissions.filter(s => s.po_generated).length;

  return (
    <div className="space-y-4">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h3 style={{ fontSize: 'var(--text-body)', fontWeight: 600 }}>{lang === 'bn' ? 'PO তৈরি — জমাকৃত হাজিরা' : 'Generate POs — Submitted Attendance'}</h3>
          <div style={{ fontSize: 11, color: 'var(--color-label-tertiary)', marginTop: 2 }}>{lang === 'bn' ? 'Generate Purchase Orders' : 'PO তৈরি করুন'}</div>
        </div>
        {pendingCount > 0 && (
          <button onClick={generateBulk} disabled={bulkRunning}
            className="btn" style={{ background: 'var(--color-red)', color: 'white', fontWeight: 600 }}>
            {bulkRunning ? '...' : (lang === 'bn' ? `সব PO তৈরি করুন (${pendingCount})` : `Generate All POs (${pendingCount})`)}
          </button>
        )}
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-4">
        <div className="stat-card" style={{ textAlign: 'center' }}><p className="stat-value">{submissions.length}</p><p className="stat-label">{lang === 'bn' ? 'মোট জমা' : 'Total Submitted'}</p></div>
        <div className="stat-card" style={{ textAlign: 'center' }}><p className="stat-value" style={{ color: 'var(--color-warning)' }}>{pendingCount}</p><p className="stat-label">{lang === 'bn' ? 'PO অপেক্ষমাণ' : 'PO Pending'}</p></div>
        <div className="stat-card" style={{ textAlign: 'center' }}><p className="stat-value" style={{ color: 'var(--color-accent)' }}>{doneCount}</p><p className="stat-label">{lang === 'bn' ? 'PO তৈরি হয়েছে' : 'PO Generated'}</p></div>
      </div>

      {/* School submissions list */}
      {submissions.map(sub => (
        <div key={sub.submission_id} className="card-surface" style={{
          padding: 'var(--space-4) var(--space-5)',
          borderLeft: `3px solid ${sub.po_generated ? 'var(--color-accent)' : 'var(--color-warning)'}`
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 'var(--text-body)', fontWeight: 600 }}>{sub.school_name || sub.school_id}</div>
              <div style={{ fontSize: 'var(--text-footnote)', color: 'var(--color-label-secondary)', marginTop: 2 }}>
                EIIN: {sub.eiin} | {lang === 'bn' ? 'মোট উপস্থিত' : 'Total Present'}: <strong>{sub.total_present}</strong> | {lang === 'bn' ? 'জমা' : 'Submitted'}: {sub.submitted_at?.slice(11, 16)}
              </div>
            </div>
            <div>
              {sub.po_generated ? (
                <span className="badge badge-success">{lang === 'bn' ? 'PO তৈরি হয়েছে' : 'PO Generated'}</span>
              ) : (
                <button onClick={() => generateSingle(sub.school_id, sub.school_name)} disabled={generating === sub.school_id}
                  className="btn btn-sm" style={{ background: 'var(--color-accent)', color: 'white' }}>
                  <Send size={14} /> {generating === sub.school_id ? '...' : (lang === 'bn' ? 'PO তৈরি করুন' : 'Generate PO')}
                </button>
              )}
            </div>
          </div>
        </div>
      ))}
      {submissions.length === 0 && (
        <div className="card-surface" style={{ textAlign: 'center', padding: 'var(--space-8)', color: 'var(--color-label-tertiary)' }}>
          {lang === 'bn' ? 'আজ কোনো বিদ্যালয় হাজিরা জমা দেয়নি' : 'No schools have submitted attendance today'}
        </div>
      )}
    </div>
  );
}

export default function MealManager() {
  const { isTeacher, role, lang } = useApp();
  const defaultTab = isTeacher ? 'record' : role === 'principal' ? 'submit' : 'generate_po';
  const [mealTab, setMealTab] = useState(defaultTab);

  // Reset tab when role changes
  React.useEffect(() => { setMealTab(defaultTab); }, [role, isTeacher, defaultTab]);

  const tabs = isTeacher
    ? [{ id: 'record', en: 'Record Meal', bn: 'খাবার রেকর্ড' }]
    : role === 'principal'
      ? [{ id: 'submit', en: 'Submit to District', bn: 'জেলায় জমা দিন' }, { id: 'analytics', en: 'Analytics', bn: 'বিশ্লেষণ' }]
      : [{ id: 'generate_po', en: 'Generate POs', bn: 'PO তৈরি করুন' }, { id: 'analytics', en: 'Analytics', bn: 'বিশ্লেষণ' }, { id: 'suppliers', en: 'Suppliers', bn: 'সরবরাহকারী' }, { id: 'po_status', en: 'PO Status', bn: 'PO স্ট্যাটাস' }];

  return (
    <div className="space-y-5">
      {tabs.length > 1 && (
        <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
          {tabs.map(tb => (
            <button key={tb.id} onClick={() => setMealTab(tb.id)} className="btn btn-sm"
              style={{ background: mealTab === tb.id ? 'var(--color-accent)' : 'var(--color-surface-2)', color: mealTab === tb.id ? 'white' : 'var(--color-label-secondary)', display: 'flex', flexDirection: 'column', padding: '8px 16px', gap: 1 }}>
              <span style={{ fontWeight: 500 }}>{lang === 'bn' ? tb.bn : tb.en}</span>
              <span style={{ fontSize: 9, opacity: 0.5 }}>{lang === 'bn' ? tb.en : tb.bn}</span>
            </button>
          ))}
        </div>
      )}
      {mealTab === 'record' && <MealRecordForm />}
      {mealTab === 'submit' && <PrincipalSubmitPanel />}
      {mealTab === 'generate_po' && <DistrictPOGeneration />}
      {mealTab === 'analytics' && <MealAnalytics />}
      {mealTab === 'suppliers' && <SupplierManagement />}
      {mealTab === 'po_status' && <POStatusDashboard />}
    </div>
  );
}
